'use client';

import SelectOption from '@molecules/Select/Option';
import SelectBox from '@molecules/Select/SelectBox';
import { useEffect, useState } from 'react';
import { searchStores } from '@api/interfaces/storeApi';
import { FacetSearchPageData } from '@occ/api-client';
import Text from '@atoms/Text/Text';
import Link from '@atoms/Link/Link';
import { useTranslations } from 'next-intl';
import paths from '@constants/paths';
import {
  StyledSelect,
  StyledCategoryHeader,
  StyledHeadingCity,
  StyledHeadingOpeningHours,
  StyledHeadingStore,
  StyledStoreItem,
  StyledStoresWrapper,
  StyledStoreInfo,
  StyledShowMoreButton,
  StyledBodyOpeningHours,
  StyledPageLayout,
} from './StoreListPage.styles';
import useResponsive from '@hooks/useResponsive';
import Heading from '@atoms/Heading/Heading';

interface Props {
  pickupStores?: boolean;
}

const StoreListPageClient = ({ pickupStores = false }: Props) => {
  const t = useTranslations('storeListPage');
  const [stores, setStores] = useState<FacetSearchPageData | undefined>();
  const [fetchSize, setFetchSize] = useState<number>(10);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { isMobile } = useResponsive();

  const defaultSortSelection = {
    value: 'display-name-asc',
    label: t('sort.display-name-asc'),
    selected: true,
  };

  const sortSelections = [
    defaultSortSelection,
    {
      value: 'display-name-desc',
      label: t('sort.display-name-desc'),
      selected: false,
    },
    {
      value: 'town-asc',
      label: t('sort.town-asc'),
      selected: false,
    },
    {
      value: 'town-desc',
      label: t('sort.town-desc'),
      selected: false,
    },
  ];

  const [selectedOption, setSelectedOption] = useState<OptionType>(defaultSortSelection);
  const [selectedOptionList, setSelectedOptionList] = useState<OptionType[]>(sortSelections);

  const fetchStores = async (size: number, sort: string) => {
    let result;
    if (pickupStores) {
      result = await searchStores('', sort, size, false, false, true);
    } else {
      result = await searchStores('', sort, size);
    }
    setStores(result);
  };

  const onChangeHandler = async (option: OptionType) => {
    setSelectedOptionList((previousOptions) =>
      previousOptions.map((previousOption) => ({ ...previousOption, selected: previousOption.value === option.value }))
    );
    setSelectedOption(option);
    await fetchStores(fetchSize, option.value);
  };

  const showMore = async () => {
    setIsSubmitting(true);
    await fetchStores(fetchSize + 20, selectedOption.value);
    if ((stores?.pagination?.totalNumberOfResults as number) < fetchSize + 20) {
      setFetchSize(0);
    } else {
      setFetchSize(fetchSize + 20);
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    fetchStores(fetchSize, selectedOption.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledPageLayout>
      <Heading variant="h2">{pickupStores ? t('clickAndCollect.heading') : t('all.heading')}</Heading>

      <StyledSelect
        required
        options={selectedOptionList}
        testId="select-box-selector"
        optionComponent={({ option, isSelected, onItemClickHandler }) => (
          <SelectOption
            key={option.value}
            option={option}
            isSelected={isSelected}
            onItemClick={() => {
              onItemClickHandler(option);
            }}
          />
        )}
        selectBoxComponent={() => <SelectBox type="subtitle" size="small" currentSelection={selectedOption} />}
        onChange={onChangeHandler}
        avoidViewport
      />

      <StyledCategoryHeader>
        <StyledHeadingStore type="label">{t('storeHeading')}</StyledHeadingStore>
        <StyledHeadingCity type="label">{t('cityHeading')}</StyledHeadingCity>
        <StyledHeadingOpeningHours type="label">{t('openingHoursHeading')}</StyledHeadingOpeningHours>
      </StyledCategoryHeader>

      {stores?.results && (
        <StyledStoresWrapper>
          {stores.results.map((store) => {
            const addressLine = `${store.address.line1}, ${store.address.postalCode} ${store.address.town}`;
            return (
              <StyledStoreItem key={addressLine}>
                <StyledStoreInfo>
                  <Text type="subhead">{store.displayName}</Text>
                  <Text type="body">{addressLine}</Text>
                  <Text type="body">{`${t('tel')} ${store.address.phoneNumber ? store.address.phoneNumber : ''}`}</Text>
                  {isMobile && (
                    <StyledBodyOpeningHours type="body">{`${t('openingHoursBody')} ${
                      store.todaysOpeningHours
                    }`}</StyledBodyOpeningHours>
                  )}
                  <Link href={`${paths.STORE_PAGE}/${store.storeId}`}>{t('storeLink')}</Link>
                </StyledStoreInfo>
                <StyledHeadingCity type="body">{store.address.town}</StyledHeadingCity>
                <StyledHeadingOpeningHours type="body">{store.todaysOpeningHours}</StyledHeadingOpeningHours>
              </StyledStoreItem>
            );
          })}
        </StyledStoresWrapper>
      )}

      {fetchSize > 0 && (
        <StyledShowMoreButton onClick={() => showMore()} isSubmitting={isSubmitting}>
          {t('showMore')}
        </StyledShowMoreButton>
      )}
    </StyledPageLayout>
  );
};

export default StoreListPageClient;
